// Mini-player hook — реальный playback через HTMLAudioElement + MediaSession.
//
// PHASE-4-AUDIO-SUMMARY.md §9.1, §9.4 + LEARNING-SYSTEM-VISION §6.6.
//
// Связь со store:
//   - `recapId` смена → меняем источник (офлайн-blob либо presigned URL)
//   - `isPlaying` → audioElement.play() / pause()
//   - `seek(pos)` → audioElement.currentTime = pos
//   - `speed`/`volume` → mirror на audioElement
//   - audioElement.ontimeupdate → обновляет store.positionSec
//   - audioElement.onloadedmetadata → store.setDuration
//   - audioElement.onended → pause (detail) / close (mini-player elsewhere)
//
// Здесь же живут два сквозных эффекта прослушивания:
//   1. Телеметрия `audio_recap_playback_log` через PowerSync (PUT на старте,
//      PATCH позиции раз в ~10 сек) — офлайн-friendly, без tRPC-шума.
//   2. Автозакрытие активности темы при прослушивании ≥ AUDIO_LISTENED_RATIO —
//      ОДНОКРАТНО на сессию (ref-guard), иначе каждый timeupdate бил бы мутацией.
//
// `<audio>` создаётся один раз на life-cycle приложения и живёт в МОДУЛЬНЫХ
// переменных (singleton на вкладку) — не превращать в useRef-инстанс.
import { AUDIO_LISTENED_RATIO } from "@studyhub/shared/learning";
import { useEffect, useRef, useState } from "react";
import { uuidv7 } from "uuidv7";
// Единственный предикат воркспейса курса на всё приложение (декрет E8) —
// копия здесь была бы расхождением при первой же правке роутов.
import { isWorkspaceRoute } from "@/components/layout/_layout-state";
import { useAudioOfflinePrefetch } from "@/hooks/use-audio-offline-prefetch";
import { useSession } from "@/hooks/use-session";
import { audioOfflineCache } from "@/lib/audio-offline-cache";
import { getLastKnownOnline } from "@/lib/network-status";
import { IS_CAPACITOR } from "@/lib/platform-detect";
import { insertRow, updateRow } from "@/lib/powersync/db";
import { api } from "@/lib/trpc-client";
import { useAudioRecapStore } from "@/stores/audio-recap";

let audioElement: HTMLAudioElement | null = null;
/** Recap, для которого СТАРТОВАЛА привязка источника (async-резолв кэша). */
/** Пересборок источника после ошибки загрузки — на один recap. */
const MAX_SRC_RETRIES = 2;

let lastBoundRecapId: string | null = null;
/** Recap, для которого источник УЖЕ проставлен в `audio.src`. */
let srcReadyRecapId: string | null = null;
/** objectURL офлайн-blob'а: обязателен revoke при смене трека, иначе течёт память. */
let currentObjectUrl: string | null = null;

/** Как часто пишем позицию в лог — это телеметрия, а не resume-позиция. */
const LOG_FLUSH_MS = 10_000;
/** Разрыв больше пары секунд — это seek, а не прослушивание. */
const MAX_LISTEN_DELTA_SEC = 2;

// Preview→full swap УДАЛЁН (AUDIO-RECAP-REWORK §Phase 6.1 откат).
// UX: пользователь ждёт полное аудио, без 30-sec preview-first. Это
// устраняет рассинхрон длительности (UI показывал 30 сек до F5) и
// упрощает audio binding logic.

function ensureAudioElement(): HTMLAudioElement | null {
	if (typeof window === "undefined") return null;
	if (audioElement) return audioElement;
	audioElement = new Audio();
	audioElement.preload = "metadata";
	// БЕЗ crossOrigin="use-credentials": src — presigned S3 GET (getStreamUrl),
	// а не проксируемый /api/.../stream с cookie-auth. Presigned URL работает
	// в Capacitor (Bearer-клиент, `<audio>` не шлёт Authorization) и на
	// S3-домене cookie всё равно не уходит — credentials ломают CORS.
	return audioElement;
}

function releaseObjectUrl(): void {
	if (!currentObjectUrl) return;
	URL.revokeObjectURL(currentObjectUrl);
	currentObjectUrl = null;
}

/** Состояние одной сессии прослушивания (для лога и автозакрытия активности). */
interface PlaybackSession {
	recapId: string | null;
	sessionId: string;
	/** id строки `audio_recap_playback_log`; `null` — ещё не создана. */
	rowId: string | null;
	creating: boolean;
	listenedSec: number;
	lastPositionSec: number;
	lastFlushAt: number;
	completed: boolean;
}

function newSession(recapId: string | null): PlaybackSession {
	return {
		recapId,
		sessionId: uuidv7(),
		rowId: null,
		creating: false,
		listenedSec: 0,
		lastPositionSec: 0,
		lastFlushAt: 0,
		completed: false,
	};
}

export function useMiniPlayer() {
	const state = useAudioRecapStore();
	const initialized = useRef(false);
	const { user } = useSession();
	const userId = user?.id ?? null;

	// Офлайн-предзагрузка живёт здесь же: это единственный хук, смонтированный
	// на всё приложение (AudioPlayerEngine в __root) и отвечающий за аудио.
	useAudioOfflinePrefetch();

	// Инвалидация по успеху ОБЯЗАТЕЛЬНА: сервер на закрытие ОДНОЙ активности
	// socket-событие не шлёт, а экран темы читает статусы из `courses.get`
	// (staleTime 30с) — без неё шаг ① по аудио оставался бы незакрытым до
	// перезагрузки, и «Завершить тему» не разблокировалась бы.
	const utils = api.useUtils();
	const completeActivity = api.courses.completeActivityByReference.useMutation({
		onSuccess: (res) => {
			if (res.completed) void utils.courses.invalidate();
		},
	});
	const completeRef = useRef(completeActivity.mutate);
	completeRef.current = completeActivity.mutate;

	const sessionRef = useRef<PlaybackSession>(newSession(null));

	// Presigned S3 GET URL текущего recap'а (кросс-платформенно — работает
	// в Capacitor Bearer-клиенте). Сервер выдаёт inline-disposition URL с
	// TTL 1ч; смена recapId → новый запрос, query-кэш дедуплицирует.
	const streamQuery = api.audioRecaps.recaps.getStreamUrl.useQuery(
		{ recapId: state.recapId ?? "" } as never,
		{
			enabled: !!state.recapId,
			staleTime: 50 * 60 * 1000,
			// Офлайн не ретраим (URL всё равно не понадобится — играет копия из
			// кэша), а вот единичный сетевой обрыв в онлайне раньше оставлял
			// плеер вообще без источника до перезагрузки страницы.
			// `getLastKnownOnline` — оценка общего монитора сети без своей пробы:
			// решение принимается внутри ретрая, бить сюда HEAD-ом незачем.
			// `null` (монитор ещё не отвечал) читаем как «онлайн» — тогда ретрай
			// разрешён, и он же покажет правду.
			retry: (failureCount) => failureCount < 1 && getLastKnownOnline() !== false,
		},
	);
	const streamUrl = (streamQuery.data as { url?: string } | undefined)?.url ?? null;

	/**
	 * Сколько раз пересобирали источник ПОСЛЕ ошибки загрузки для текущего
	 * recap'а. Без этого счётчика битый файл дал бы бесконечный цикл
	 * «ошибка → перезапрос URL → ошибка».
	 */
	const [srcAttempt, setSrcAttempt] = useState(0);
	// Новый recap — новый бюджет попыток.
	// biome-ignore lint/correctness/useExhaustiveDependencies: сброс именно на смену recap'а, setter стабилен
	useEffect(() => {
		setSrcAttempt(0);
	}, [state.recapId]);

	// Ошибка загрузки источника — не тупик. Presigned URL живёт час, сеть на
	// мобильном рвётся, S3 закрывает соединение — и до этой правки одного такого
	// обрыва хватало, чтобы плеер умер до перезагрузки страницы: `src` оставался
	// битым, привязка считалась выполненной, и `play()` дальше падал
	// `NotSupportedError: no supported sources` (жалоба 2026-08-19).
	// Теперь снимаем привязку, просим свежий URL и пересобираем источник.
	useEffect(() => {
		const audio = ensureAudioElement();
		const recapId = state.recapId;
		if (!audio || !recapId) return;
		const onError = (): void => {
			useAudioRecapStore.getState().pause();
			if (srcAttempt >= MAX_SRC_RETRIES) {
				// eslint-disable-next-line no-console -- слышимой альтернативы нет, диагностика нужна
				console.warn("Audio source failed after retries", { recapId });
				return;
			}
			lastBoundRecapId = null;
			srcReadyRecapId = null;
			void utils.audioRecaps.recaps.getStreamUrl.invalidate({ recapId } as never);
			setSrcAttempt((attempt) => attempt + 1);
		};
		audio.addEventListener("error", onError);
		return () => audio.removeEventListener("error", onError);
	}, [state.recapId, srcAttempt, utils]);

	useEffect(() => {
		const audio = ensureAudioElement();
		if (!audio || initialized.current) return;
		initialized.current = true;

		audio.addEventListener("loadedmetadata", () => {
			useAudioRecapStore.getState().setDuration(audio.duration || 0);
		});
		audio.addEventListener("timeupdate", () => {
			// Не вызываем seek() — он рекурсивно ставит currentTime, ловим loop.
			useAudioRecapStore.setState({ positionSec: audio.currentTime });
		});
		audio.addEventListener("ended", () => {
			// На detail page оставляем paused store (RecapPlayerBar живёт на
			// recapId); уход со страницы закроет его cleanup'ом. То же в воркспейсе
			// курса: там плеер сквозной (живёт при переходе между шагами и темами,
			// §2.3) — close() по концу трека уничтожил бы состояние сцены аудио.
			// На остальных маршрутах мини-плеер после конца трека не нужен — close.
			const path = window.location.pathname;
			const onRecapDetail = /^\/audio-recaps\/(?!new(?:\/|$)|share(?:\/|$))[^/]+/.test(path);
			if (onRecapDetail || isWorkspaceRoute(path)) {
				useAudioRecapStore.getState().pause();
			} else {
				useAudioRecapStore.getState().close();
			}
		});

		// MediaSession API — lock-screen controls (web). На Capacitor дублируется
		// нативным плеером в Phase 4 follow-up через `packages/platform/audio`.
		if ("mediaSession" in navigator) {
			navigator.mediaSession.setActionHandler("play", () => {
				useAudioRecapStore.getState().play();
			});
			navigator.mediaSession.setActionHandler("pause", () => {
				useAudioRecapStore.getState().pause();
			});
			navigator.mediaSession.setActionHandler("seekbackward", () => {
				const cur = useAudioRecapStore.getState();
				cur.seek(Math.max(0, cur.positionSec - 15));
			});
			navigator.mediaSession.setActionHandler("seekforward", () => {
				const cur = useAudioRecapStore.getState();
				cur.seek(Math.min(cur.durationSec, cur.positionSec + 15));
			});
		}
	}, []);

	// Bind источника при смене recapId. Сначала пробуем офлайн-копию: она играет
	// без сети и не тратит трафик, presigned URL — фолбэк.
	useEffect(() => {
		const audio = ensureAudioElement();
		if (!audio) return;

		if (!state.recapId) {
			if (lastBoundRecapId) {
				audio.pause();
				audio.removeAttribute("src");
				audio.load();
				releaseObjectUrl();
				lastBoundRecapId = null;
				srcReadyRecapId = null;
			}
			return;
		}

		// НЕ гейтим bind на streamUrl: `getStreamUrl` — сетевой запрос
		// (retry:false, вне persist-whitelist), офлайн он не резолвится никогда.
		// Сначала всегда читаем офлайн-копию, presigned URL — фолбэк; иначе
		// скачанное аудио не игралось бы ровно в метро, ради чего слой и делался.
		if (state.recapId === lastBoundRecapId) return;

		const recapId = state.recapId;
		const trackTitle = state.trackTitle;
		const resumeSec = state.positionSec;
		// Помечаем сразу: пока резолвится кэш, эффект не должен стартовать повторно.
		lastBoundRecapId = recapId;
		let cancelled = false;

		void audioOfflineCache.getBlob(recapId).then((blob) => {
			if (cancelled || lastBoundRecapId !== recapId) return;
			if (!blob && !streamUrl) {
				// Ни офлайн-копии, ни presigned URL — снимаем метку, чтобы эффект
				// повторился, когда query всё-таки резолвится (сеть вернулась).
				lastBoundRecapId = null;
				return;
			}
			releaseObjectUrl();
			if (blob) {
				currentObjectUrl = URL.createObjectURL(blob);
				audio.src = currentObjectUrl;
			} else if (streamUrl) {
				audio.src = streamUrl;
			}
			audio.load();
			srcReadyRecapId = recapId;

			if (resumeSec > 0) {
				const onMeta = (): void => {
					audio.currentTime = resumeSec;
					audio.removeEventListener("loadedmetadata", onMeta);
				};
				audio.addEventListener("loadedmetadata", onMeta);
			}

			if ("mediaSession" in navigator && trackTitle) {
				navigator.mediaSession.metadata = new MediaMetadata({
					title: trackTitle,
					artist: "StudyHub",
				});
			}

			// Пока резолвился источник, пользователь мог нажать play — эффект
			// ниже отработал вхолостую (src ещё не было), стартуем здесь.
			if (useAudioRecapStore.getState().isPlaying) {
				void audio.play().catch(() => useAudioRecapStore.getState().pause());
			}
		});

		return () => {
			cancelled = true;
		};
		// state.positionSec читается для одноразового resume при смене
		// recapId, но НЕ в deps намеренно (пишется ~4 Гц в timeupdate).
	}, [state.recapId, state.trackTitle, streamUrl, srcAttempt]);

	// Новая сессия прослушивания на каждый новый recap.
	useEffect(() => {
		sessionRef.current = newSession(state.recapId);
	}, [state.recapId]);

	// Bind isPlaying. play() только когда источник уже привязан — иначе play()
	// на пустом src бросит и сразу pause().
	useEffect(() => {
		const audio = ensureAudioElement();
		if (!audio || !state.recapId) return;
		if (state.isPlaying) {
			if (srcReadyRecapId !== state.recapId || !audio.src) return;
			void audio.play().catch((err) => {
				// eslint-disable-next-line no-console -- audio autoplay policy
				console.warn("Audio play() rejected", err);
				useAudioRecapStore.getState().pause();
			});
		} else {
			audio.pause();
		}
	}, [state.isPlaying, state.recapId, streamUrl]);

	// Bind speed + volume.
	useEffect(() => {
		const audio = ensureAudioElement();
		if (!audio) return;
		audio.playbackRate = state.speed;
		audio.volume = state.volume;
	}, [state.speed, state.volume]);

	// Bind user-инициированный seek → audio.currentTime. Реагируем только
	// на рост seekNonce (scrubber / ±15 / setRecap), не на timeupdate-поток,
	// который пишет positionSec напрямую — иначе seek-feedback loop.
	// biome-ignore lint/correctness/useExhaustiveDependencies: seekNonce — намеренный signal-only dep; positionSec читаем через getState() чтобы не re-run'ить на каждый timeupdate (4 Hz)
	useEffect(() => {
		const audio = ensureAudioElement();
		if (!audio || !state.recapId) return;
		const target = useAudioRecapStore.getState().positionSec;
		// metadata ещё не загружена → seekable пуст, currentTime молча
		// сбросится в 0; отложим до loadedmetadata.
		if (!Number.isFinite(audio.duration) || audio.duration === 0) {
			const onMeta = () => {
				audio.currentTime = target;
				audio.removeEventListener("loadedmetadata", onMeta);
			};
			audio.addEventListener("loadedmetadata", onMeta);
			return () => audio.removeEventListener("loadedmetadata", onMeta);
		}
		if (Math.abs(audio.currentTime - target) > 0.5) {
			audio.currentTime = target;
		}
	}, [state.seekNonce, state.recapId]);

	// Телеметрия + автозакрытие активности. Работает от store, а не от DOM-события:
	// timeupdate уже пишет positionSec, а хук всё равно ре-рендерится (~4 Гц).
	useEffect(() => {
		if (!state.recapId || !userId || !state.isPlaying) return;
		void advancePlayback({
			session: sessionRef.current,
			recapId: state.recapId,
			userId,
			positionSec: state.positionSec,
			durationSec: state.durationSec,
			speed: state.speed,
			onListened: (recapId) => {
				completeRef.current({ type: "audio-recap", referenceId: recapId });
			},
		});
	}, [state.recapId, state.isPlaying, state.positionSec, state.durationSec, state.speed, userId]);

	return state;
}

interface AdvanceArgs {
	session: PlaybackSession;
	recapId: string;
	userId: string;
	positionSec: number;
	durationSec: number;
	speed: number;
	onListened: (recapId: string) => void;
}

/**
 * Один шаг прослушивания: копит «наслушано», раз в {@link LOG_FLUSH_MS} пишет
 * позицию и ровно один раз закрывает активность темы на пороге
 * {@link AUDIO_LISTENED_RATIO}.
 *
 * PowerSync-запись — best effort: локальная SQLite может быть не готова
 * (cold start / logout), и телеметрия не имеет права ломать воспроизведение.
 */
async function advancePlayback(args: AdvanceArgs): Promise<void> {
	const { session, recapId, userId, positionSec, durationSec, speed } = args;
	if (session.recapId !== recapId) return;

	const delta = positionSec - session.lastPositionSec;
	if (delta > 0 && delta < MAX_LISTEN_DELTA_SEC) session.listenedSec += delta;
	session.lastPositionSec = positionSec;

	const reachedThreshold =
		durationSec > 0 && positionSec / durationSec >= AUDIO_LISTENED_RATIO && !session.completed;

	// Ref-guard выставляем СИНХРОННО, до первого await: порог пересекается на
	// каждом timeupdate (~4 Гц), и асинхронный флаг пропустил бы вторую мутацию.
	if (reachedThreshold) {
		session.completed = true;
		args.onListened(recapId);
	}

	await writePlaybackLog({ session, recapId, userId, positionSec, speed, reachedThreshold });
}

async function writePlaybackLog(args: {
	session: PlaybackSession;
	recapId: string;
	userId: string;
	positionSec: number;
	speed: number;
	reachedThreshold: boolean;
}): Promise<void> {
	const { session, recapId, userId, positionSec, speed, reachedThreshold } = args;
	const throttled = Date.now() - session.lastFlushAt < LOG_FLUSH_MS;

	try {
		if (!session.rowId) {
			if (session.creating) return;
			session.creating = true;
			session.rowId = await insertRow("audio_recap_playback_log", {
				audio_recap_id: recapId,
				user_id: userId,
				session_id: session.sessionId,
				platform: IS_CAPACITOR ? "capacitor" : "web",
				position_sec: 0,
				duration_listened_sec: 0,
				playback_speed: String(speed),
			});
			session.creating = false;
			session.lastFlushAt = Date.now();
			return;
		}
		if (throttled && !reachedThreshold) return;
		await updateRow("audio_recap_playback_log", session.rowId, {
			position_sec: Math.round(positionSec),
			duration_listened_sec: Math.round(session.listenedSec),
			playback_speed: String(speed),
			...(reachedThreshold ? { completed_at: new Date().toISOString() } : {}),
		});
		session.lastFlushAt = Date.now();
	} catch {
		session.creating = false;
		// PowerSync недоступен — телеметрия не критична.
	}
}
